import { useMemo, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

type CCCanvasModelConfig = {
  name: string;
  api: string;
};

const MODEL_CONFIG_STORAGE_KEY = "cccanvas.ai.modelConfigs";
const DEFAULT_MODELS: CCCanvasModelConfig[] = Array.from(
  { length: 6 },
  (_, i) => ({
    name: `自定义大模型${i + 1}`,
    api: "",
  }),
);

const RATIOS = [
  { label: "智能", value: "auto", width: 2048, height: 2048 },
  { label: "21:9", value: "21:9", width: 2560, height: 1080 },
  { label: "16:9", value: "16:9", width: 2048, height: 1152 },
  { label: "3:2", value: "3:2", width: 2048, height: 1365 },
  { label: "4:3", value: "4:3", width: 2048, height: 1536 },
  { label: "1:1", value: "1:1", width: 2048, height: 2048 },
  { label: "3:4", value: "3:4", width: 1536, height: 2048 },
  { label: "2:3", value: "2:3", width: 1365, height: 2048 },
  { label: "9:16", value: "9:16", width: 1152, height: 2048 },
];

const normalizeModelConfigs = (
  modelConfigs: Partial<CCCanvasModelConfig>[] = [],
) =>
  DEFAULT_MODELS.map((defaultModel, index) => ({
    name: modelConfigs[index]?.name || defaultModel.name,
    api: modelConfigs[index]?.api || defaultModel.api,
  }));

const loadModelConfigs = () => {
  const savedModelConfigs = localStorage.getItem(MODEL_CONFIG_STORAGE_KEY);

  if (!savedModelConfigs) {
    return DEFAULT_MODELS;
  }

  try {
    return normalizeModelConfigs(JSON.parse(savedModelConfigs));
  } catch {
    return DEFAULT_MODELS;
  }
};

export const CCCanvasAIPanel = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI;
}) => {
  const [prompt, setPrompt] = useState("");
  const [models, setModels] = useState<CCCanvasModelConfig[]>(loadModelConfigs);
  const [selectedModelIndex, setSelectedModelIndex] = useState(0);
  const [selectedRatio, setSelectedRatio] = useState(RATIOS[5]);
  const [resolution, setResolution] = useState<"2K" | "4K">("2K");
  const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
  const [isRatioMenuOpen, setIsRatioMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const selectedModel = models[selectedModelIndex];
  const size = useMemo(() => {
    if (resolution === "4K") {
      return {
        width: selectedRatio.width * 2,
        height: selectedRatio.height * 2,
      };
    }

    return {
      width: selectedRatio.width,
      height: selectedRatio.height,
    };
  }, [resolution, selectedRatio]);

  const persistModels = (nextModels: CCCanvasModelConfig[]) => {
    setModels(nextModels);
    localStorage.setItem(MODEL_CONFIG_STORAGE_KEY, JSON.stringify(nextModels));
  };

  const updateModel = (
    index: number,
    key: keyof CCCanvasModelConfig,
    value: string,
  ) => {
    persistModels(
      models.map((model, modelIndex) =>
        modelIndex === index ? { ...model, [key]: value } : model,
      ),
    );
  };

  const handleGenerate = () => {
    excalidrawAPI.setToast({
      message: `已选择 ${selectedModel.name}，${selectedRatio.label}，${resolution}，${size.width}×${size.height}。图片生成接口已预留。`,
    });
  };

  return (
    <>
      <div className="cccanvas-ai-toolbar" aria-label="CCCanvas AI actions">
        <button type="button">添加到对话</button>
        <button type="button">重新编辑</button>
        <button type="button">再次生成</button>
        <button type="button">扩图</button>
        <button type="button">超清</button>
        <button type="button">抠图</button>
        <button type="button">改文字</button>
        <button type="button">多角度</button>
        <button type="button">···</button>
      </div>

      <div
        className="cccanvas-ai-dock"
        aria-label="CCCanvas AI image generation panel"
      >
        <div className="cccanvas-ai-dock__upload" aria-hidden="true">
          +
        </div>
        <textarea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder="上传参考图、输入文字或 @ 主体，描述你想生成的图片。"
          rows={2}
        />
        <div className="cccanvas-ai-dock__controls">
          <button
            className="cccanvas-ai-chip cccanvas-ai-chip--active"
            type="button"
          >
            图片生成
          </button>

          <div className="cccanvas-ai-popover-wrap">
            <button
              className="cccanvas-ai-chip"
              type="button"
              onClick={() => {
                setIsModelMenuOpen(!isModelMenuOpen);
                setIsRatioMenuOpen(false);
              }}
            >
              {selectedModel.name}
            </button>
            {isModelMenuOpen && (
              <div className="cccanvas-ai-popover cccanvas-ai-popover--models">
                <p>选择模型：{selectedModel.name}</p>
                {models.map((model, index) => (
                  <button
                    className={
                      index === selectedModelIndex ? "is-selected" : ""
                    }
                    type="button"
                    key={index}
                    onClick={() => {
                      setSelectedModelIndex(index);
                      setIsModelMenuOpen(false);
                    }}
                  >
                    <span>{model.name}</span>
                    <small>{model.api ? "已配置 API" : "等待配置 API"}</small>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="cccanvas-ai-popover-wrap">
            <button
              className="cccanvas-ai-chip"
              type="button"
              onClick={() => {
                setIsRatioMenuOpen(!isRatioMenuOpen);
                setIsModelMenuOpen(false);
              }}
            >
              {selectedRatio.label} · {resolution}
            </button>
            {isRatioMenuOpen && (
              <div className="cccanvas-ai-popover cccanvas-ai-popover--ratio">
                <p>选择比例</p>
                <div className="cccanvas-ai-ratio-grid">
                  {RATIOS.map((ratio) => (
                    <button
                      className={
                        ratio.value === selectedRatio.value ? "is-selected" : ""
                      }
                      type="button"
                      key={ratio.value}
                      onClick={() => setSelectedRatio(ratio)}
                    >
                      {ratio.label}
                    </button>
                  ))}
                </div>
                <p>选择分辨率</p>
                <div className="cccanvas-ai-resolution-toggle">
                  {(["2K", "4K"] as const).map((value) => (
                    <button
                      className={resolution === value ? "is-selected" : ""}
                      type="button"
                      key={value}
                      onClick={() => setResolution(value)}
                    >
                      {value === "2K" ? "高清 2K" : "超清 4K ✦"}
                    </button>
                  ))}
                </div>
                <p>尺寸</p>
                <div className="cccanvas-ai-size-row">
                  <span>W</span>
                  <strong>{size.width}</strong>
                  <span>×</span>
                  <span>H</span>
                  <strong>{size.height}</strong>
                  <span>PX</span>
                </div>
              </div>
            )}
          </div>

          <button
            className="cccanvas-ai-chip"
            type="button"
            onClick={() => setIsSettingsOpen(true)}
          >
            大模型设置
          </button>

          <button
            className="cccanvas-ai-submit"
            type="button"
            onClick={handleGenerate}
            aria-label="生成图片"
          >
            ↑
          </button>
        </div>
      </div>

      {isSettingsOpen && (
        <div className="cccanvas-ai-settings" role="dialog" aria-modal="true">
          <div className="cccanvas-ai-settings__panel">
            <div className="cccanvas-ai-settings__header">
              <div>
                <strong>大模型设置</strong>
                <span>配置后会同步显示到 6 个大模型选择位置</span>
              </div>
              <button type="button" onClick={() => setIsSettingsOpen(false)}>
                ×
              </button>
            </div>
            <div className="cccanvas-ai-settings__list">
              {models.map((model, index) => (
                <div className="cccanvas-ai-settings__item" key={index}>
                  <span>{index + 1}</span>
                  <label>
                    大模型命名
                    <input
                      value={model.name}
                      onChange={(event) =>
                        updateModel(index, "name", event.target.value)
                      }
                      placeholder={`自定义大模型${index + 1}`}
                    />
                  </label>
                  <label>
                    对应 API
                    <input
                      value={model.api}
                      onChange={(event) =>
                        updateModel(index, "api", event.target.value)
                      }
                      placeholder="https://api.example.com/v1/images/generations"
                    />
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
